import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import {
  Box,
  Container,
  IconButton,
  SxProps,
  Theme,
  useTheme,
} from "@mui/material";
import { Typography } from "@platform/design-system";

import CirclesSVG from "../../assets/circles-svg";

import type { JSX } from "react";

interface Props {
  title: React.ReactNode;
  subtitle?: string;
  onBack?: () => void;
  extra?: React.ReactNode;
  breadcrumbs?: React.ReactNode;
}

const SectionHeader = ({
  title,
  subtitle,
  onBack,
  extra,
  breadcrumbs,
}: Props): JSX.Element => {
  const theme = useTheme();
  const styles = useStyles(theme);

  return (
    <Box data-testid="ds-section-header" sx={styles.sectionHeaderContainer}>
      <Container sx={styles.sectionHeaderContent}>
        <Box sx={styles.circlesBG} aria-hidden="true">
          <CirclesSVG />
        </Box>
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          flexWrap="wrap"
          flexGrow={1}
          gap={1}
        >
          {onBack ? (
            <IconButton
              data-testid="ds-section-header-back-button"
              onClick={onBack}
              sx={styles.backButton}
              aria-label="Back"
            >
              <ChevronLeftIcon />
            </IconButton>
          ) : null}
          <Box
            sx={styles.text}
            display="flex"
            flexDirection="column"
            flexGrow={1}
          >
            {breadcrumbs}
            <Typography
              data-testid="ds-section-header-title"
              variant="header-medium"
              aria-label="Section Title"
            >
              {title}
            </Typography>
            {subtitle ? (
              <Typography
                sx={styles.subtitle}
                data-testid="ds-section-header-subtitle"
                variant="subtitle1"
                color="textSecondary"
                aria-label="Section Sub Text"
              >
                {subtitle}
              </Typography>
            ) : null}
          </Box>
          {extra && <div data-testid="ds-section-header-extra">{extra}</div>}
        </Box>
      </Container>
    </Box>
  );
};

interface SectionHeaderStyles<T extends Theme> {
  sectionHeaderContainer: SxProps<T>;
  sectionHeaderContent: SxProps<T>;
  circlesBG: SxProps<T>;
  backButton: SxProps<T>;
  text: SxProps<T>;
  subtitle: SxProps<T>;
}

function useStyles<T extends Theme>(theme: T): SectionHeaderStyles<T> {
  return {
    sectionHeaderContainer: {
      lol: "sectionHeaderContainer",
      overflow: "hidden",
      background:
        "linear-gradient(90deg, #832A80 0%, #0078A2 50.5%, #4FB3CD 100%)",
      // Dark mode: scrim from the page ground over the gradient so the banner
      // settles into the dark chrome. Only matches under a `cssVariables` dark
      // scheme, so themes without it render the gradient unchanged.
      '[data-mui-color-scheme="dark"] &': {
        backgroundImage:
          "linear-gradient(color-mix(in srgb, var(--ds-mui-palette-background-default) 45%, transparent), color-mix(in srgb, var(--ds-mui-palette-background-default) 45%, transparent)), linear-gradient(90deg, #832A80 0%, #0078A2 50.5%, #4FB3CD 100%)",
      },
    },
    sectionHeaderContent: {
      position: "relative",
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(2),
    },
    circlesBG: {
      position: "absolute",
      top: "170%",
      right: "25%",
      transform: "translate(48%, -50%) scale(1)", // Adjust the scale value as needed
      opacity: 0.5,
      zIndex: 0,
      pointerEvents: "none",
    },
    backButton: {
      svg: {
        color: theme.palette.common.white,
      },
    },
    text: {
      color: theme.palette.common.white,
      h5: {
        color: "inherit",
        fontWeight: "regular",
      },
      h6: {
        fontWeight: "300",
      },
    },
    subtitle: {
      color: theme.palette.common.white,
      fontWeight: 300,
    },
  };
}

export { SectionHeader };
